import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import VideoRow from '../components/VideoRow';
import ModelCarousel from '../components/ModelCarousel';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import AdBanner, { InFeedAd, InFeedAd2, InFeedAd3, LeaderboardAd, LeaderboardAdBottom, StickyFooterAd, MobileAdHome } from '../components/AdBanner';
import OrientationTabs from '../components/OrientationTabs';
import { Play, Info, Crown, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeader } = useAuth();
  const [orientation, setOrientation] = useState('all');
  const [trendingVideos, setTrendingVideos] = useState([]);
  const [newestVideos, setNewestVideos] = useState([]);
  const [topRatedVideos, setTopRatedVideos] = useState([]);
  const [recommendedVideos, setRecommendedVideos] = useState([]);
  const [recommendReason, setRecommendReason] = useState('');
  const [continueWatching, setContinueWatching] = useState([]);
  const [featuredVideo, setFeaturedVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    fetchContent();
    fetchRecommendations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orientation]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore) {
          loadMoreContent();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    observerRef.current = observer;
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMore]);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const ts = Date.now();
      // For "all", fetch trending without orientation filter
      const orientationEndpoint = orientation === 'all' 
        ? `${API}/content/trending?_t=${ts}`
        : `${API}/content/orientation/${orientation}?_t=${ts}`;
      
      const [trendingRes, newestRes, topRatedRes] = await Promise.all([
        axios.get(orientationEndpoint),
        axios.get(`${API}/content/newest?_t=${ts}`),
        axios.get(`${API}/content/videos?ordering=rating&period=weekly&_t=${ts}`)
      ]);

      setTrendingVideos(trendingRes.data.videos || []);
      setNewestVideos(newestRes.data.videos || []);
      setTopRatedVideos(topRatedRes.data.videos || []);
      
      const trending = trendingRes.data.videos || [];
      if (trending.length > 0) {
        const randomIndex = Math.floor(Math.random() * Math.min(5, trending.length));
        setFeaturedVideo(trending[randomIndex]);
      }

      if (isAuthenticated) {
        try {
          const historyRes = await axios.get(`${API}/user/history`, getAuthHeader());
          setContinueWatching(historyRes.data.items?.slice(0, 10) || []);
        } catch (err) {
          console.debug('Could not fetch watch history:', err.message);
        }
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const headers = isAuthenticated ? getAuthHeader().headers : {};
      const res = await axios.get(`${API}/content/recommendations`, { headers });
      setRecommendedVideos(res.data.videos || []);
      setRecommendReason(res.data.reason || 'Recommended for you');
    } catch (err) {
      console.log('Could not fetch recommendations');
    }
  };

  const loadMoreContent = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const res = await axios.get(`${API}/content/orientation/${orientation}?page=${nextPage}`);
      const moreVideos = res.data.videos || [];
      if (moreVideos.length > 0) {
        setTrendingVideos(prev => [...prev, ...moreVideos]);
        setPage(nextPage);
      }
    } catch (err) {
      console.error('Error loading more:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleOrientationChange = (newOrientation) => {
    setOrientation(newOrientation);
  };

  const handlePlayFeatured = () => {
    if (featuredVideo) {
      navigate(`/video/${featuredVideo.id}`, { state: { video: featuredVideo } });
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="home-page">
      <SEO 
        title="Home" 
        description="Stream the hottest trending adult videos. Browse 150+ categories, discover new favorites, and enjoy premium content on VelvetStream."
        path="/"
      />

      {/* Model Carousel - replaces hero */}
      <section className="pt-16" data-testid="model-carousel-section">
        <ModelCarousel />
      </section>

      {/* Content Sections */}
      <main className="px-4 md:px-8 lg:px-16 py-8 relative z-10">
        {/* Orientation Tabs */}
        <section className="mb-8 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0" data-testid="orientation-section">
          <OrientationTabs 
            selected={orientation} 
            onChange={handleOrientationChange}
            className="justify-start md:justify-start min-w-max md:min-w-0"
          />
        </section>

        {/* Recommendations Section */}
        {recommendedVideos.length > 0 && (
          <VideoRow 
            title={recommendReason || "Recommended For You"}
            videos={recommendedVideos}
            loading={loading}
          />
        )}

        {/* Continue Watching */}
        {continueWatching.length > 0 && (
          <VideoRow 
            title="Continue Watching" 
            videos={continueWatching.map(item => ({
              ...item,
              id: item.video_id,
              progress: item.progress || 0
            }))}
            showProgress={true}
            loading={loading}
          />
        )}

        {/* Trending by Orientation */}
        <VideoRow 
          title={`Trending ${orientation.charAt(0).toUpperCase() + orientation.slice(1)}`}
          videos={trendingVideos}
          loading={loading}
          showAds={!user?.subscription_tier}
          adFrequency={3}
        />

        {/* Leaderboard Ad */}
        {!user?.subscription_tier && (
          <section className="mb-12 flex justify-center" data-testid="leaderboard-ad-1">
            <LeaderboardAd />
          </section>
        )}

        {/* Top Rated */}
        <VideoRow 
          title="Top Rated This Week"
          videos={topRatedVideos}
          loading={loading}
        />

        {/* Newest */}
        <VideoRow 
          title="New Releases" 
          videos={newestVideos}
          loading={loading}
          showAds={!user?.subscription_tier}
          adFrequency={4}
        />

        {/* Banner Ad Row - 3 unique zones */}
        {!user?.subscription_tier && (
          <section className="mb-12" data-testid="banner-ad-row">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <InFeedAd />
              <InFeedAd2 />
              <InFeedAd3 />
            </div>
          </section>
        )}

        {/* Mobile Ad */}
        {!user?.subscription_tier && (
          <section className="mb-8">
            <MobileAdHome />
          </section>
        )}

        {/* Popular Categories */}
        <section className="mb-12" data-testid="categories-banner">
          <h2 className="section-header">Popular Categories</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
            {['MILF', 'Teen 18+', 'Lesbian', 'BDSM', 'Anal', 'Creampie', 'BBW', 'Latina', 'Asian', 'Threesome', 'POV', 'Step-Family'].map((category, index) => (
              <button
                key={category}
                onClick={() => navigate(`/categories?tags=${category.toLowerCase()}`)}
                className="relative h-20 md:h-24 lg:h-32 bg-[#121212] border border-white/5 rounded-md overflow-hidden group hover:border-white/20 transition-all duration-300"
                data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <span className="absolute bottom-2 left-2 md:bottom-3 md:left-3 text-white font-medium text-xs md:text-sm uppercase tracking-wider group-hover:text-gray-300 transition-colors">
                  {category}
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* Premium CTA */}
        {!user?.subscription_tier && (
          <section className="mb-12" data-testid="premium-cta">
            <div className="bg-gradient-to-r from-purple-900/30 to-amber-900/30 border border-white/10 rounded-lg p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
                  <Crown className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-1">Go Premium</h3>
                  <p className="text-gray-400">Ad-free experience, HD streaming, and exclusive features</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/subscription')}
                className="bg-white text-black hover:bg-gray-200 px-8"
                data-testid="premium-cta-btn"
              >
                Upgrade Now
              </Button>
            </div>
          </section>
        )}

        {/* Bottom Leaderboard Ad */}
        {!user?.subscription_tier && (
          <section className="mb-12 flex justify-center" data-testid="leaderboard-ad-bottom">
            <LeaderboardAdBottom />
          </section>
        )}

        {/* Infinite Scroll Trigger */}
        <div ref={loadMoreRef} className="flex justify-center py-8" data-testid="infinite-scroll-trigger">
          {loadingMore && (
            <div className="flex items-center gap-3 text-gray-500">
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading more...</span>
            </div>
          )}
        </div>

        {/* Sticky Footer Ad */}
        {!user?.subscription_tier && (
          <StickyFooterAd />
        )}
      </main>
    </div>
  );
};

export default Home;
